/**
 * Game Configuration
 * Central source of truth for game dimensions, colors, typography, and visual settings.
 * Values derived from DESIGN.md specifications.
 */

// =============================================================================
// GAME DIMENSIONS
// =============================================================================

export const GAME_WIDTH = 375;
export const GAME_HEIGHT = 812;

/** Safe areas for mobile devices (notch, home indicator) */
export const SAFE_AREA = {
  top: 44,      // Status bar / notch
  bottom: 34,   // Home indicator gesture area
} as const;

/** Layout zones */
export const LAYOUT = {
  hudHeight: 64,
  armyZoneHeight: 120,
  gameplayArea: GAME_HEIGHT - SAFE_AREA.top - SAFE_AREA.bottom - 64 - 120,
} as const;

// =============================================================================
// COLOR PALETTE
// =============================================================================

export const COLORS = {
  // Primary game colors
  player: 0x39FF14,
  playerGlow: 0x39FF14,  // Use with alpha for glow effects
  enemy: 0xFF6B35,
  enemyDark: 0xC23A22,
  gate: 0x39FF14,
  gateGlow: 0x39FF14,    // Use with alpha for glow effects
  gold: 0xFFD93D,
  goldDark: 0xE5C235,

  // Backgrounds — the command-deck palette keeps menus and gameplay in one world.
  bgGameplay: 0x071522,
  bgMenu: 0x07131F,
  bgOverlay: 0x02070D,
  bgTrack: 0x102A3C,
  grid: 0x1B4055,

  // UI colors
  uiText: 0xEAF7FF,
  uiTextSecondary: 0x8BA9BC,
  uiButtonPrimary: 0x173B2B,
  uiButtonSecondary: 0x122536,
  uiSuccess: 0x39FF14,
  uiWarning: 0xFFD93D,
} as const;

/** CSS color strings for text/HTML elements */
export const COLOR_CSS = {
  player: '#39FF14',
  playerGlow: '#39FF1440',
  enemy: '#FF6B35',
  enemyDark: '#C23A22',
  gate: '#39FF14',
  gateGlow: '#39FF1440',
  gold: '#FFD93D',
  goldDark: '#E5C235',
  bgGameplay: '#071522',
  bgMenu: '#07131F',
  bgOverlay: '#02070D',
  bgTrack: '#102A3C',
  grid: '#1B4055',
  uiText: '#EAF7FF',
  uiTextSecondary: '#8BA9BC',
} as const;

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const FONTS = {
  display: 'Oxanium, Rajdhani, system-ui, sans-serif',
  body: 'Rajdhani, Nunito, system-ui, sans-serif',
  numbers: 'Share Tech Mono, Rajdhani, monospace',
} as const;

export const TEXT_SIZES = {
  hero: 72,      // Title screen logo
  display: 48,   // Victory messages, big numbers
  headline: 32,  // Screen titles
  title: 24,     // Level names, section headers
  body: 18,      // Body text, descriptions
  caption: 14,   // Labels, secondary info
  tiny: 12,      // Legal text, timestamps
} as const;

// =============================================================================
// SPACING (8px base unit)
// =============================================================================

export const SPACING = {
  space1: 4,   // Tight spacing, inline elements
  space2: 8,   // Default small gap
  space3: 16,  // Standard padding
  space4: 24,  // Section gaps
  space5: 32,  // Large gaps
  space6: 48,  // Screen margins
  space7: 64,  // Major section breaks
} as const;

// =============================================================================
// ANIMATION EASING
// =============================================================================

/** Easing functions as cubic bezier arrays [x1, y1, x2, y2] */
export const EASING = {
  standard: [0.4, 0.0, 0.2, 1],
  bounce: [0.34, 1.56, 0.64, 1],
  out: [0.0, 0.0, 0.2, 1],
  in: [0.4, 0.0, 1, 1],
  pop: [0.175, 0.885, 0.32, 1.275],
} as const;

/** Animation durations in milliseconds */
export const DURATIONS = {
  buttonDown: 100,
  buttonUp: 150,
  screenTransition: 250,
  multiplyPop: 200,
  enemyDeath: 150,
  scoreIncrement: 300,
  multiplierPopup: 600,
  screenShake: 200,
  hitFlash: 50,
  pulseGlow: 1500,
} as const;

// =============================================================================
// ENTITY SIZES (relative to screen width)
// =============================================================================

export const ENTITY_SIZES = {
  /** Player soldier size as percentage of screen width */
  soldierPercent: 0.03,
  /** Basic enemy size as percentage of screen width */
  basicEnemyPercent: 0.04,
  /** Elite enemy size as percentage of screen width */
  eliteEnemyPercent: 0.06,
  /** Gate width as percentage of screen width */
  gateWidthPercent: 0.38,
  /** Gate height as percentage of screen height */
  gateHeightPercent: 0.076,
  /** Weapon box size as percentage of screen width */
  weaponBoxPercent: 0.15,
} as const;

/** Calculated pixel sizes */
export const ENTITY_PIXELS = {
  soldier: Math.round(GAME_WIDTH * ENTITY_SIZES.soldierPercent),
  basicEnemy: Math.round(GAME_WIDTH * ENTITY_SIZES.basicEnemyPercent),
  eliteEnemy: Math.round(GAME_WIDTH * ENTITY_SIZES.eliteEnemyPercent),
  gateWidth: Math.round(GAME_WIDTH * ENTITY_SIZES.gateWidthPercent),
  gateHeight: Math.round(GAME_HEIGHT * ENTITY_SIZES.gateHeightPercent),
  weaponBox: Math.round(GAME_WIDTH * ENTITY_SIZES.weaponBoxPercent),
} as const;

/** Gate-specific layout tuning for spacing and collisions */
export const GATE_LAYOUT = {
  horizontalGapPx: 22,
  collisionInsetX: 8,
  collisionInsetY: 6,
} as const;

// =============================================================================
// UI COMPONENT SIZES
// =============================================================================

export const UI_SIZES = {
  /** Minimum touch target size per NFR-011 */
  minTouchTarget: 44,
  /** Icon button size */
  iconButton: 48,
  /** Primary button border radius */
  buttonRadiusPrimary: 16,
  /** Secondary button border radius */
  buttonRadiusSecondary: 12,
  /** Progress bar height */
  progressBarHeight: 8,
} as const;

// =============================================================================
// PHASER GAME CONFIG
// =============================================================================

/**
 * Creates the Phaser game configuration.
 * This is a function rather than a constant to avoid referencing Phaser
 * at module load time, which allows config.ts to be imported in tests.
 */
function getRendererType(Phaser: typeof import('phaser')): number {
  if (typeof window === 'undefined') {
    return Phaser.AUTO;
  }

  const params = new URLSearchParams(window.location.search);
  const rendererParam = params.get('renderer');
  const isTestMode = params.get('test') === '1';

  if (rendererParam === 'canvas' || isTestMode) {
    return Phaser.CANVAS;
  }
  if (rendererParam === 'webgl') {
    return Phaser.WEBGL;
  }

  return Phaser.AUTO;
}

export function createPhaserConfig(Phaser: typeof import('phaser')): import('phaser').Types.Core.GameConfig {
  return {
    type: getRendererType(Phaser),
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
    parent: 'game-container',
    backgroundColor: COLORS.bgGameplay,
    pixelArt: true,
    roundPixels: true,
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    physics: {
      default: 'arcade',
      arcade: {
        gravity: { x: 0, y: 0 },
        debug: false,
      },
    },
    render: {
      pixelArt: true,
      antialias: false,
    },
  };
}
